import React, { useState, useMemo, useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import PlatformShell from '../../components/PlatformShell';
import { Badge, DataTable, StatCard } from '../../components/Ui';
import {
  CountGrid, StaffRoleSection,
  patientCols, queueCols, receiptCols, admissionCols, bedCols,
  pharmacyCols, labCols, emrCols, auditCols,
} from '../../components/platform/MonitorPanels';
import PlatformStaffEditor from '../../components/platform/PlatformStaffEditor';
import {
  ExportBar, QueueActions, PatientEditRow, BedAddForm, BedRateEdit,
  LabStatusEdit, PartialDataBanner, LoadErrorBanner,
} from '../../components/platform/PlatformTabTools';
import { ipApi } from '../../services/api';
import { useHospitalMonitor } from '../../hooks/useHospitalMonitor';
import {
  PLAN_PRESETS, STAFF_ROLES, ALL_MODULES, modulesForPlan, roleLimitsForPlan, totalRoleLimit, pricingForPlan,
} from '../../config/plans';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'doctors', label: 'Doctors', countKey: 'doctors' },
  { id: 'staff', label: 'All staff', countKey: 'staff' },
  { id: 'patients', label: 'Patients', countKey: 'patients' },
  { id: 'queue', label: 'OP Queue', countKey: 'queueActive' },
  { id: 'billing', label: 'Billing', countKey: 'receiptsAll' },
  { id: 'ipd', label: 'IPD & Beds', countKey: 'admissionsActive' },
  { id: 'pharmacy', label: 'Pharmacy', countKey: 'pharmacyOrders' },
  { id: 'lab', label: 'Laboratory', countKey: 'labOrders' },
  { id: 'emr', label: 'EMR', countKey: 'emrRecords' },
  { id: 'audit', label: 'Activity', countKey: 'auditEvents' },
  { id: 'revenue', label: 'Revenue' },
  { id: 'plan', label: 'Plan & amounts' },
  { id: 'limits', label: 'Limits' },
  { id: 'oplink', label: 'Link with OP' },
  { id: 'edit', label: 'Settings' },
];

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: '7 days' },
  { id: 'month', label: 'Month' },
  { id: 'year', label: 'Year' },
  { id: 'all', label: 'All time' },
];

function formatInr(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN')}`;
}

function QuotaBars({ quotas }) {
  if (!quotas) return null;
  return (
    <div className="quota-bars">
      {STAFF_ROLES.map((r) => {
        const limit = quotas.limits?.[r.id] ?? 0;
        const used = quotas.usage?.[r.id] ?? 0;
        const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
        return (
          <div key={r.id} className="quota-bar-row">
            <span className="quota-bar-label">{r.label}</span>
            <div className="quota-bar-track"><div className="quota-bar-fill" style={{ width: `${pct}%` }} /></div>
            <span className="quota-bar-count">{used}/{limit}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function PlatformHospitalDetail() {
  const { hospitalId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'overview');
  const [period, setPeriod] = useState('month');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [newOwnerPassword, setNewOwnerPassword] = useState('');

  const { data, loading, error, partial, reload } = useHospitalMonitor(hospitalId, period);

  const hospital = data?.hospital;
  const counts = data?.counts || {};
  const staff = data?.staff || [];
  const staffByRole = data?.staffByRole || {};
  const quotas = data?.quotas;
  const stats = data?.stats || {};
  const patients = data?.patients || [];
  const queue = data?.queue || [];
  const receipts = data?.receipts || [];
  const admissions = data?.admissions || [];
  const beds = data?.beds || [];
  const pharmacyOrders = data?.pharmacyOrders || [];
  const labOrders = data?.labOrders || [];
  const emrRecords = data?.emrRecords || [];
  const auditLog = data?.auditLog || [];
  const timeline = data?.timeline || [];

  const [editForm, setEditForm] = useState(null);
  const [limitForm, setLimitForm] = useState(null);
  const [pricingForm, setPricingForm] = useState(null);
  const [opLinkCode, setOpLinkCode] = useState('');
  const [opLinkPaste, setOpLinkPaste] = useState('');
  const [opLinkExpires, setOpLinkExpires] = useState('');
  const [opLinkLocal, setOpLinkLocal] = useState(null);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && TABS.some((x) => x.id === t)) setTab(t);
  }, [searchParams]);

  const selectTab = (id) => {
    setTab(id);
    if (id === 'overview') setSearchParams({}, { replace: true });
    else setSearchParams({ tab: id }, { replace: true });
  };

  useEffect(() => {
    if (hospital && !editForm) {
      setEditForm({
        name: hospital.name || '',
        addressLine1: hospital.addressLine1 || '',
        addressLine2: hospital.addressLine2 || '',
        pincode: hospital.pincode || '',
        phone: hospital.phone || '',
        email: hospital.email || '',
        gstin: hospital.gstin || '',
        tagline: hospital.tagline || '',
        regNo: hospital.regNo || '',
        website: hospital.website || '',
        city: hospital.city || '',
        state: hospital.state || 'AP',
        plan: hospital.plan || 'Standard',
        slug: hospital.slug || '',
        active: hospital.active !== false,
        modules: hospital.modules || modulesForPlan(hospital.plan),
        ownerEmail: hospital.ownerEmail || '',
        notes: hospital.notes || '',
        logoUrl: hospital.logoUrl || '',
        letterheadColor: hospital.letterheadColor || '#0d6e6a',
        billingContact: hospital.billingContact || '',
        subscriptionStatus: hospital.subscriptionStatus || 'active',
        letterheadOnPdf: hospital.letterheadOnPdf !== false,
      });
    }
    if (hospital && !limitForm) {
      setLimitForm({ ...(hospital.roleLimits || roleLimitsForPlan(hospital.plan)) });
    }
    if (hospital && !pricingForm) {
      setPricingForm({ ...(hospital.pricing || pricingForPlan(hospital.plan)) });
    }
  }, [hospital, editForm, limitForm, pricingForm]);

  useEffect(() => {
    if (hospital) {
      setOpLinkLocal({
        opHospitalId: hospital.opHospitalId || '',
        opHospitalName: hospital.opHospitalName || '',
        opLinkedAt: hospital.opLinkedAt || '',
        linked: !!(hospital.opHospitalId && String(hospital.opHospitalId).trim()),
      });
    }
  }, [hospital]);

  const owner = useMemo(() => staff.find((s) => s.isHospitalOwner || s.role === 'manager'), [staff]);

  const generateOpLinkCode = async () => {
    setBusy('oplink');
    setMsg('');
    try {
      const r = await ipApi.platformGenerateOpLink(hospitalId);
      setOpLinkCode(r.code || '');
      setOpLinkExpires(r.expiresAt || '');
      setMsg('IP link code generated — paste it in OP Admin → Link with IP.');
    } catch (e) {
      setMsg(e.message || 'Failed to generate link code');
    } finally {
      setBusy(false);
    }
  };

  const acceptOpLinkCode = async () => {
    setBusy('oplink');
    setMsg('');
    try {
      const r = await ipApi.platformAcceptOpLink(hospitalId, { code: opLinkPaste.trim() });
      setOpLinkLocal({
        opHospitalId: r.opHospitalId || '',
        opHospitalName: r.opHospitalName || '',
        opLinkedAt: r.opLinkedAt || '',
        linked: true,
      });
      setOpLinkPaste('');
      setMsg(r.message || 'Linked with OP hospital.');
      reload();
    } catch (e) {
      setMsg(e.message || 'Failed to link');
    } finally {
      setBusy(false);
    }
  };

  const unlinkOp = async () => {
    if (!window.confirm('Unlink this hospital from OP? Patient data on both sides is kept; only the hospital connection is removed.')) return;
    setBusy('oplink');
    setMsg('');
    try {
      const r = await ipApi.platformUnlinkOp(hospitalId);
      setOpLinkLocal({ opHospitalId: '', opHospitalName: '', opLinkedAt: '', linked: false });
      setMsg(r.message || 'Unlinked from OP.');
      reload();
    } catch (e) {
      setMsg(e.message || 'Failed to unlink');
    } finally {
      setBusy(false);
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setMsg('Copied to clipboard');
    } catch (_) {
      setMsg('Copy failed — select and copy manually');
    }
  };

  const saveEdit = async () => {
    setBusy('edit');
    setMsg('');
    try {
      await ipApi.platformPatchHospital(hospitalId, {
        name: editForm.name.trim(),
        addressLine1: editForm.addressLine1.trim(),
        addressLine2: editForm.addressLine2,
        pincode: editForm.pincode,
        phone: editForm.phone,
        email: editForm.email,
        gstin: editForm.gstin,
        tagline: editForm.tagline,
        regNo: editForm.regNo,
        website: editForm.website,
        city: editForm.city,
        state: editForm.state,
        plan: editForm.plan,
        slug: editForm.slug,
        active: editForm.active,
        modules: editForm.modules,
        ownerEmail: editForm.ownerEmail,
        notes: editForm.notes,
        logoUrl: editForm.logoUrl,
        letterheadColor: editForm.letterheadColor,
        billingContact: editForm.billingContact,
        subscriptionStatus: editForm.subscriptionStatus,
        letterheadOnPdf: editForm.letterheadOnPdf !== false,
      });
      setMsg('Hospital updated.');
      await reload();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy('');
    }
  };

  const saveLimits = async () => {
    setBusy('limits');
    setMsg('');
    try {
      await ipApi.platformPatchHospital(hospitalId, {
        roleLimits: limitForm,
        staffLimit: totalRoleLimit(limitForm),
      });
      setMsg('Login limits updated.');
      await reload();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy('');
    }
  };

  const applyPlan = (plan) => {
    setEditForm((f) => ({ ...f, plan, modules: modulesForPlan(plan) }));
    setLimitForm(roleLimitsForPlan(plan));
    setPricingForm(pricingForPlan(plan));
  };

  const savePricing = async (applyToBeds = false) => {
    setBusy('pricing');
    setMsg('');
    try {
      await ipApi.platformPatchHospital(hospitalId, {
        pricing: pricingForm,
        plan: editForm?.plan || hospital?.plan,
        modules: editForm?.modules || hospital?.modules,
      });
      if (applyToBeds) {
        const res = await ipApi.platformPatchBedRates(hospitalId, {
          rates: pricingForm,
          applyPricingToHospital: true,
        });
        setMsg(`Pricing saved · ${res.bedsUpdated ?? 0} bed rates updated.`);
      } else {
        setMsg('Plan amounts saved.');
      }
      await reload();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy('');
    }
  };

  const toggleModule = (modId) => {
    setEditForm((f) => {
      const mods = f.modules || [];
      const has = mods.includes(modId);
      return { ...f, modules: has ? mods.filter((m) => m !== modId) : [...mods, modId] };
    });
  };

  const toggleActive = async () => {
    const isActive = hospital?.active !== false;
    if (isActive && !window.confirm(`Suspend ${hospital?.name}?`)) return;
    if (!isActive && !window.confirm(`Reactivate ${hospital?.name}?`)) return;
    setBusy('active');
    try {
      await ipApi.platformPatchHospital(hospitalId, { active: !isActive });
      await reload();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy('');
    }
  };

  const deleteHospital = async () => {
    if (!window.confirm(`Archive ${hospital?.name}? Staff logins will be disabled. You can restore later from Settings.`)) return;
    setBusy('delete');
    try {
      await ipApi.platformDeleteHospital(hospitalId);
      navigate('/platform/dashboard');
    } catch (e) {
      setMsg(e.message);
      setBusy('');
    }
  };

  const restoreHospital = async () => {
    if (!window.confirm(`Restore ${hospital?.name} to active hospitals?`)) return;
    setBusy('restore');
    try {
      await ipApi.platformRestoreHospital(hospitalId);
      setMsg('Hospital restored.');
      await reload();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy('');
    }
  };

  const resetOwnerPassword = async () => {
    setBusy('reset');
    try {
      const res = await ipApi.platformResetOwnerPassword(hospitalId);
      setNewOwnerPassword(res.temporaryPassword);
      setMsg('Owner password reset.');
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy('');
    }
  };

  if (loading && !hospital) {
    return (
      <PlatformShell title="Loading hospital…">
        <p className="hint">Loading hospital data — your login stays active.</p>
      </PlatformShell>
    );
  }

  if (!hospital && !loading) {
    return (
      <PlatformShell title="Could not load hospital">
        <LoadErrorBanner error={error || 'Unable to load this hospital right now.'} onRetry={reload} />
        <Link to="/platform/dashboard" className="btn btn-secondary mt">← All hospitals</Link>
      </PlatformShell>
    );
  }

  return (
    <PlatformShell
      title={hospital?.name || 'Hospital'}
      subtitle={`Full monitor · ${hospital?.city || ''} · ${hospital?.plan} · ${hospitalId}`}
      actions={
        <>
          <Badge tone={hospital?.active !== false ? 'success' : 'warning'}>
            {hospital?.active !== false ? 'Active' : 'Suspended'}
          </Badge>
          <button type="button" className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => reload()}>Refresh all</button>
          <button type="button" className="btn btn-secondary btn-sm" disabled={!!busy} onClick={toggleActive}>
            {hospital?.active !== false ? 'Suspend' : 'Reactivate'}
          </button>
        </>
      }
    >
      <div className="platform-hospital-toolbar card mb">
        <Link to="/platform/dashboard" className="btn btn-secondary btn-sm">← All hospitals</Link>
        <p className="hint monitor-sync">Live snapshot · {data?.generatedAt ? `Updated ${data.generatedAt.slice(11, 19)} UTC` : ''}</p>
        <div className="platform-tab-bar monitor-tabs">
          {TABS.map((t) => (
            <button key={t.id} type="button" className={`tab-chip ${tab === t.id ? 'active' : ''}`} onClick={() => selectTab(t.id)}>
              {t.label}{t.countKey != null ? ` (${counts[t.countKey] ?? 0})` : ''}
            </button>
          ))}
        </div>
      </div>

      {hospital?.deletedAt && (
        <div className="error mb">
          This hospital is <strong>archived</strong> (removed {hospital.deletedAt.slice(0, 10)}).{' '}
          <button type="button" className="btn btn-sm btn-primary" disabled={busy === 'restore'} onClick={restoreHospital}>
            {busy === 'restore' ? 'Restoring…' : 'Restore hospital'}
          </button>
        </div>
      )}

      {msg && <p className={`mb ${msg.includes('updated') || msg.includes('reset') || msg.includes('created') || msg.includes('saved') || msg.includes('restored') ? 'hint' : 'error'}`}>{msg}</p>}
      <LoadErrorBanner error={error} onRetry={reload} />
      <PartialDataBanner partial={partial} onRetry={reload} />

      {tab === 'overview' && (
        <>
          <div className="stat-grid">
            <StatCard label="OP revenue (period)" value={formatInr(stats.opReceiptsTotal)} accent="#10b981" />
            <StatCard label="OP revenue (all time)" value={formatInr(counts.opRevenueAll)} accent="#059669" />
            <StatCard label="IP outstanding" value={formatInr(stats.ipOutstanding)} accent="#ef4444" />
            <StatCard label="Staff active" value={`${counts.staffActive}/${counts.staff}`} accent="#4f8ef7" />
          </div>
          <div className="card mb mt">
            <h3>Everything in this hospital (counts)</h3>
            <CountGrid counts={counts} />
          </div>
          <div className="form-grid">
            <div className="card">
              <h3>Profile</h3>
              <dl className="credential-dl">
                <dt>Owner</dt><dd>{hospital?.ownerEmail || owner?.email || '—'}</dd>
                <dt>Slug</dt><dd><code>{hospital?.slug}.hcen</code></dd>
                <dt>Modules</dt><dd>{(hospital?.modules || []).join(', ') || '—'}</dd>
                <dt>Monthly HCEN</dt><dd>{formatInr(hospital?.pricing?.monthlyFee)}</dd>
                <dt>Subscription</dt><dd>{hospital?.subscriptionStatus || 'active'}</dd>
                <dt>OP linked</dt><dd>{hospital?.opHospitalId ? `Yes · ${hospital.opHospitalName || hospital.opHospitalId}` : 'No'}</dd>
                <dt>Onboarded</dt><dd>{(hospital?.createdAt || '').slice(0, 10)}</dd>
              </dl>
            </div>
            <div className="card">
              <h3>Login limits</h3>
              <QuotaBars quotas={quotas} />
            </div>
          </div>
          <div className="card mt">
            <h3>Quick manage</h3>
            <div className="role-chip-row">
              {TABS.filter((t) => t.id !== 'overview').map((t) => (
                <button key={t.id} type="button" className="emr-chip" onClick={() => selectTab(t.id)}>
                  {t.label}{t.countKey != null ? ` (${counts[t.countKey] ?? 0})` : ''}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {tab === 'doctors' && (
        <StaffRoleSection title="Doctors" rows={staffByRole.doctor || []} />
      )}

      {tab === 'staff' && (
        <>
          <div className="card mb row-actions" style={{ justifyContent: 'space-between' }}>
            <p className="hint">Owner login — reset if they forgot password.</p>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy === 'reset'} onClick={resetOwnerPassword}>Reset owner password</button>
          </div>
          {newOwnerPassword && (
            <div className="owner-credentials card mb">
              <strong>Owner new password:</strong> <code>{newOwnerPassword}</code>
            </div>
          )}
          <PlatformStaffEditor
            hospitalId={hospitalId}
            hospital={hospital}
            quotas={quotas}
            staff={staff}
            onReload={reload}
            onMessage={setMsg}
          />
        </>
      )}

      {tab === 'patients' && (
        <div className="card">
          <ExportBar label={`All patients (${patients.length})`} rows={patients} columns={[
            { key: 'patientId', label: 'UHID' },
            { key: 'name', label: 'Name' },
            { key: 'phone', label: 'Phone' },
            { key: 'age', label: 'Age' },
            { key: 'gender', label: 'Gender' },
          ]} />
          <DataTable
            columns={[
              ...patientCols,
              {
                key: 'actions',
                label: 'Actions',
                render: (r) => <PatientEditRow hospitalId={hospitalId} row={r} onDone={reload} />,
              },
            ]}
            rows={patients}
            keyField="patientId"
          />
        </div>
      )}

      {tab === 'queue' && (
        <div className="card">
          <ExportBar label={`OP queue (${queue.length})`} rows={queue} columns={[
            { key: 'queueId', label: 'ID' },
            { key: 'patientName', label: 'Patient' },
            { key: 'doctorName', label: 'Doctor' },
            { key: 'status', label: 'Status' },
          ]} />
          <DataTable
            columns={[
              ...queueCols,
              {
                key: 'actions',
                label: 'Update status',
                render: (r) => <QueueActions hospitalId={hospitalId} row={r} onDone={reload} />,
              },
            ]}
            rows={queue}
            keyField="queueId"
          />
        </div>
      )}

      {tab === 'billing' && (
        <div className="card">
          <ExportBar label={`Receipts (${receipts.length})`} rows={receipts} columns={[
            { key: 'receiptId', label: 'Receipt' },
            { key: 'patientName', label: 'Patient' },
            { key: 'amount', label: 'Amount', csv: (r) => r.amount || r.total },
            { key: 'createdAt', label: 'Date' },
          ]} />
          <div className="period-tabs mb">
            {PERIODS.map((p) => (
              <button key={p.id} type="button" className={`emr-chip ${period === p.id ? 'active' : ''}`} onClick={() => setPeriod(p.id)}>{p.label}</button>
            ))}
          </div>
          <DataTable columns={receiptCols} rows={receipts} keyField="receiptId" />
        </div>
      )}

      {tab === 'ipd' && (
        <>
          <div className="card mb">
            <h3>IP admissions ({admissions.length})</h3>
            <DataTable columns={admissionCols} rows={admissions} keyField="admissionId" />
          </div>
          <div className="card">
            <div className="row-actions mb" style={{ justifyContent: 'space-between' }}>
              <h3>Bed master ({beds.length})</h3>
              <BedAddForm hospitalId={hospitalId} onDone={reload} />
            </div>
            <DataTable
              columns={[
                ...bedCols,
                {
                  key: 'rateEdit',
                  label: 'Edit rate',
                  render: (r) => <BedRateEdit hospitalId={hospitalId} row={r} onDone={reload} />,
                },
              ]}
              rows={beds}
              keyField="bedId"
            />
          </div>
        </>
      )}

      {tab === 'pharmacy' && (
        <>
          <StaffRoleSection title="Pharmacy staff logins" rows={staffByRole.pharmacy || []} />
          <div className="card">
            <h3>Pharmacy orders ({pharmacyOrders.length})</h3>
            <DataTable columns={pharmacyCols} rows={pharmacyOrders} keyField="orderId" />
          </div>
        </>
      )}

      {tab === 'lab' && (
        <>
          <StaffRoleSection title="Laboratory staff logins" rows={staffByRole.lab || []} />
          <div className="card">
            <ExportBar label={`Lab orders (${labOrders.length})`} rows={labOrders} columns={[
              { key: 'orderId', label: 'Order' },
              { key: 'patientName', label: 'Patient' },
              { key: 'testName', label: 'Test' },
              { key: 'status', label: 'Status' },
            ]} />
            <DataTable
              columns={[
                ...labCols,
                {
                  key: 'actions',
                  label: 'Status',
                  render: (r) => <LabStatusEdit hospitalId={hospitalId} row={r} onDone={reload} />,
                },
              ]}
              rows={labOrders}
              keyField="orderId"
            />
          </div>
        </>
      )}

      {tab === 'emr' && (
        <div className="card">
          <h3>EMR / consultation records ({emrRecords.length})</h3>
          <DataTable columns={emrCols} rows={emrRecords} keyField="recordId" />
        </div>
      )}

      {tab === 'audit' && (
        <div className="card">
          <h3>Activity log ({auditLog.length})</h3>
          <p className="hint">Logins, staff created, hospital updates — last 200 events.</p>
          <DataTable columns={auditCols} rows={auditLog} keyField="auditId" />
        </div>
      )}

      {tab === 'revenue' && (
        <>
          <div className="period-tabs card mb">
            {PERIODS.map((p) => (
              <button key={p.id} type="button" className={`emr-chip ${period === p.id ? 'active' : ''}`} onClick={() => setPeriod(p.id)}>{p.label}</button>
            ))}
          </div>
          <div className="stat-grid">
            <StatCard label="OP collected" value={formatInr(stats.opReceiptsTotal)} accent="#10b981" />
            <StatCard label="Receipts" value={stats.receiptCount ?? 0} />
            <StatCard label="New patients" value={stats.newPatients ?? 0} />
            <StatCard label="IP outstanding" value={formatInr(stats.ipOutstanding)} />
          </div>
          {timeline.length > 0 && (
            <div className="card mt">
              <h3>Revenue timeline</h3>
              <ul className="simple-list">
                {timeline.map((t) => <li key={t.date}>{t.date}: {formatInr(t.amount)}</li>)}
              </ul>
            </div>
          )}
        </>
      )}

      {tab === 'plan' && pricingForm && (
        <div className="form-grid">
          <div className="card">
            <h3>HCEN subscription & default fees</h3>
            <p className="hint">Monthly HCEN fee and default OP / bed rates for this hospital.</p>
            <div className="plan-cards mb">
              {Object.keys(PLAN_PRESETS).map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`plan-card ${hospital?.plan === key ? 'selected' : ''}`}
                  onClick={() => {
                    applyPlan(key);
                    setEditForm((f) => f ? { ...f, plan: key, modules: modulesForPlan(key) } : f);
                  }}
                >
                  <strong>{key}</strong>
                  <span className="hint">{formatInr(PLAN_PRESETS[key].pricing.monthlyFee)}/mo</span>
                </button>
              ))}
            </div>
            <div className="field"><label>Monthly HCEN fee (₹)</label><input className="input" type="number" min={0} value={pricingForm.monthlyFee ?? ''} onChange={(e) => setPricingForm({ ...pricingForm, monthlyFee: Number(e.target.value) || 0 })} /></div>
            <div className="field"><label>Default OP consult fee (₹)</label><input className="input" type="number" min={0} value={pricingForm.opConsultFee ?? ''} onChange={(e) => setPricingForm({ ...pricingForm, opConsultFee: Number(e.target.value) || 0 })} /></div>
            <div className="field"><label>General ward bed / day (₹)</label><input className="input" type="number" min={0} value={pricingForm.generalBedRate ?? ''} onChange={(e) => setPricingForm({ ...pricingForm, generalBedRate: Number(e.target.value) || 0 })} /></div>
            <div className="field"><label>Private room bed / day (₹)</label><input className="input" type="number" min={0} value={pricingForm.privateBedRate ?? ''} onChange={(e) => setPricingForm({ ...pricingForm, privateBedRate: Number(e.target.value) || 0 })} /></div>
            <div className="field"><label>ICU bed / day (₹)</label><input className="input" type="number" min={0} value={pricingForm.icuBedRate ?? ''} onChange={(e) => setPricingForm({ ...pricingForm, icuBedRate: Number(e.target.value) || 0 })} /></div>
            <div className="form-actions row-actions">
              <button type="button" className="btn btn-primary" disabled={busy === 'pricing'} onClick={() => savePricing(false)}>Save amounts</button>
              <button type="button" className="btn btn-secondary" disabled={busy === 'pricing'} onClick={() => savePricing(true)}>Save & apply to all beds</button>
            </div>
          </div>
          <div className="card">
            <h3>Current pricing summary</h3>
            <dl className="credential-dl">
              <dt>Plan tier</dt><dd>{hospital?.plan || '—'}</dd>
              <dt>Monthly HCEN</dt><dd>{formatInr(hospital?.pricing?.monthlyFee ?? pricingForm.monthlyFee)}</dd>
              <dt>OP consult default</dt><dd>{formatInr(hospital?.pricing?.opConsultFee ?? pricingForm.opConsultFee)}</dd>
              <dt>Beds in system</dt><dd>{beds.length}</dd>
            </dl>
            {beds.length > 0 && (
              <div className="mt">
                <h4>Bed rates (current)</h4>
                <DataTable columns={bedCols} rows={beds} keyField="bedId" />
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'limits' && limitForm && (
        <div className="card">
          <h3>Edit login limits</h3>
          <div className="quota-edit-grid">
            {STAFF_ROLES.map((r) => (
              <div key={r.id} className="quota-edit-row">
                <div><strong>{r.label}</strong></div>
                <input className="input quota-input" type="number" min={0} value={limitForm[r.id] ?? 0} onChange={(e) => setLimitForm({ ...limitForm, [r.id]: Math.max(0, Number(e.target.value) || 0) })} />
              </div>
            ))}
          </div>
          <button type="button" className="btn btn-primary mt" disabled={busy === 'limits'} onClick={saveLimits}>Save limits</button>
        </div>
      )}

      {tab === 'oplink' && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Link with HCEN OP (hospitals.hcen.in)</h3>
          <p className="hint">
            OP and IP run in <strong>separate AWS accounts</strong>. Linking uses a signed pairing code
            (no shared database). Set the same <code>HCEN_CROSS_LINK_SECRET</code> on both Lambdas in production.
          </p>

          <div className="info-banner mb" style={{
            background: opLinkLocal?.linked ? '#f0fdf4' : '#fff7ed',
            border: `1px solid ${opLinkLocal?.linked ? '#86efac' : '#fed7aa'}`,
            color: opLinkLocal?.linked ? '#166534' : '#9a3412',
          }}>
            <strong>{opLinkLocal?.linked ? '✓ Linked with OP' : '○ Not linked'}</strong>
            {opLinkLocal?.linked && (
              <>
                <div style={{ marginTop: 6, fontSize: 13 }}>
                  OP Hospital ID: <code>{opLinkLocal.opHospitalId}</code>
                  {opLinkLocal.opHospitalName ? ` · ${opLinkLocal.opHospitalName}` : ''}
                </div>
                {opLinkLocal.opLinkedAt && <div style={{ fontSize: 12, marginTop: 4 }}>Linked at: {opLinkLocal.opLinkedAt}</div>}
              </>
            )}
          </div>

          <div className="form-grid mb">
            <div className="field full">
              <label>This IP hospital ID (Networks HMS)</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input className="input" readOnly value={hospitalId} style={{ flex: 1, minWidth: 200, fontFamily: 'monospace' }} />
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => copyText(hospitalId)}>Copy IP ID</button>
              </div>
            </div>
          </div>

          <h4>Step 1 — Generate IP code for OP Admin</h4>
          <p className="hint">Generate a unique <code>IP-…</code> code, then paste it in OP Super Admin → this hospital → Link with IP.</p>
          <div className="row-actions mb">
            <button type="button" className="btn btn-primary" disabled={busy === 'oplink'} onClick={generateOpLinkCode}>
              {busy === 'oplink' ? 'Working…' : 'Generate IP link code'}
            </button>
          </div>
          {opLinkCode && (
            <div className="field full mb">
              <label>IP link code (share with OP admin · expires {opLinkExpires ? new Date(opLinkExpires).toLocaleString() : 'in 7 days'})</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <textarea className="input" readOnly rows={3} value={opLinkCode} style={{ flex: 1, minWidth: 240, fontFamily: 'monospace', fontSize: 12 }} />
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => copyText(opLinkCode)}>Copy code</button>
              </div>
            </div>
          )}

          <h4>Step 2 — Paste OP code here</h4>
          <p className="hint">In OP Admin, Generate OP link code, then paste the <code>OP-…</code> code below and click Link.</p>
          <div className="field full mb">
            <label>OP link code from OP Admin</label>
            <textarea
              className="input"
              rows={3}
              placeholder="OP-…"
              value={opLinkPaste}
              onChange={(e) => setOpLinkPaste(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </div>
          <div className="row-actions">
            <button type="button" className="btn btn-primary" disabled={busy === 'oplink' || !opLinkPaste.trim()} onClick={acceptOpLinkCode}>
              Link with OP
            </button>
            {opLinkLocal?.linked && (
              <button type="button" className="btn btn-secondary" disabled={busy === 'oplink'} onClick={unlinkOp} style={{ color: '#b91c1c' }}>
                Unlink OP
              </button>
            )}
          </div>
        </div>
      )}

      {tab === 'edit' && editForm && (
        <div className="card form-grid">
          <h3>Hospital settings</h3>
          <div className="field"><label>Name</label><input className="input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
          <div className="field full"><label>Street address</label><input className="input" value={editForm.addressLine1} onChange={(e) => setEditForm({ ...editForm, addressLine1: e.target.value })} /></div>
          <div className="field full"><label>Address line 2</label><input className="input" value={editForm.addressLine2} onChange={(e) => setEditForm({ ...editForm, addressLine2: e.target.value })} /></div>
          <div className="field"><label>City</label><input className="input" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} /></div>
          <div className="field"><label>State</label><input className="input" value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} /></div>
          <div className="field"><label>Pincode</label><input className="input" value={editForm.pincode} onChange={(e) => setEditForm({ ...editForm, pincode: e.target.value })} /></div>
          <div className="field"><label>Phone</label><input className="input" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
          <div className="field"><label>Email</label><input className="input" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
          <div className="field"><label>GSTIN</label><input className="input" value={editForm.gstin} onChange={(e) => setEditForm({ ...editForm, gstin: e.target.value })} /></div>
          <div className="field full"><label>Tagline</label><input className="input" value={editForm.tagline} onChange={(e) => setEditForm({ ...editForm, tagline: e.target.value })} /></div>
          <div className="field"><label>Registration no.</label><input className="input" value={editForm.regNo} onChange={(e) => setEditForm({ ...editForm, regNo: e.target.value })} /></div>
          <div className="field"><label>Website</label><input className="input" value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} /></div>
          <div className="field"><label>Slug</label><input className="input" value={editForm.slug} onChange={(e) => setEditForm({ ...editForm, slug: e.target.value })} /></div>
          <div className="field"><label>Owner email</label><input className="input" value={editForm.ownerEmail} onChange={(e) => setEditForm({ ...editForm, ownerEmail: e.target.value })} /><p className="hint">Also updates owner staff login email on save.</p></div>
          <div className="field full"><label>Logo URL (PDF letterhead)</label><input className="input" value={editForm.logoUrl} onChange={(e) => setEditForm({ ...editForm, logoUrl: e.target.value })} placeholder="https://…/logo.png" /></div>
          <div className="field"><label>Letterhead accent color</label><input className="input" type="color" value={editForm.letterheadColor || '#0d6e6a'} onChange={(e) => setEditForm({ ...editForm, letterheadColor: e.target.value })} /></div>
          <div className="field"><label>Billing contact</label><input className="input" value={editForm.billingContact} onChange={(e) => setEditForm({ ...editForm, billingContact: e.target.value })} placeholder="accounts@hospital.in" /></div>
          <div className="field">
            <label>Subscription status</label>
            <select className="input" value={editForm.subscriptionStatus} onChange={(e) => setEditForm({ ...editForm, subscriptionStatus: e.target.value })}>
              {['active', 'trial', 'past_due', 'paused', 'cancelled'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="field full">
            <label>Modules enabled</label>
            <div className="role-chip-row">
              {ALL_MODULES.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={`emr-chip ${(editForm.modules || []).includes(m.id) ? 'active' : ''}`}
                  onClick={() => toggleModule(m.id)}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <div className="field full">
            <div className="plan-cards">
              {Object.keys(PLAN_PRESETS).map((key) => (
                <button key={key} type="button" className={`plan-card ${editForm.plan === key ? 'selected' : ''}`} onClick={() => applyPlan(key)}>{key}</button>
              ))}
            </div>
          </div>
          <div className="field full">
            <label>Internal notes</label>
            <textarea className="input" rows={3} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} placeholder="HCEN-only notes about this hospital" />
          </div>
          <div className="field">
            <label className="pdf-lh-toggle">
              <input type="checkbox" checked={editForm.letterheadOnPdf !== false} onChange={(e) => setEditForm({ ...editForm, letterheadOnPdf: e.target.checked })} />
              <span>Letterhead on PDFs</span>
            </label>
          </div>
          <div className="field">
            <label><input type="checkbox" checked={editForm.active} onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })} /> Hospital active</label>
          </div>
          <div className="form-actions full row-actions">
            <button type="button" className="btn btn-primary" disabled={busy === 'edit'} onClick={saveEdit}>Save settings</button>
            <button type="button" className="btn btn-danger btn-sm" onClick={deleteHospital}>Remove hospital</button>
          </div>
        </div>
      )}
    </PlatformShell>
  );
}
